/**
 * Sprint 24 — AgentEquipmentEditor.
 *
 * Settings-tab Access editor for an agent's equipment (S24.48). Reads the
 * junction (useAgentEquipment) joined against the unified connection list, shows
 * mixed MCP + Wrapped chips with type-coloured dots, and adds/removes equipment
 * inline via equip/unequip — replacing the old connection_scope multi-select.
 */

import { useMemo, useState, type CSSProperties } from "react";
import {
  type UnifiedConnection,
  useAgentEquipment,
  useUnifiedConnections,
  useEquipAgent,
  useUnequipAgent,
  chipTypeFor,
  equippedDotKind,
} from "../../../hooks/use-connections";
import { ConnectionTypeChip } from "../connections/ConnectionTypeChip";
import { DOT_COLOR } from "../connections/EquippedWithChip";
import { EquippedWithLegend } from "../connections/EquippedWithChip";

export interface AgentEquipmentEditorProps {
  nodeId: string;
  canEdit: boolean;
}

export function AgentEquipmentEditor({ nodeId, canEdit }: AgentEquipmentEditorProps) {
  const equipmentQ = useAgentEquipment(nodeId);
  const connectionsQ = useUnifiedConnections();
  const equip = useEquipAgent();
  const unequip = useUnequipAgent();
  const [adding, setAdding] = useState(false);

  const byKey = useMemo(() => {
    const m = new Map<string, UnifiedConnection>();
    for (const c of connectionsQ.data ?? []) m.set(`${c.kind}:${c.connection_id}`, c);
    return m;
  }, [connectionsQ.data]);

  const equippedKeys = useMemo(
    () => new Set((equipmentQ.data ?? []).map((e) => `${e.connection_kind}:${e.connection_id}`)),
    [equipmentQ.data],
  );

  const equipped = useMemo(
    () =>
      (equipmentQ.data ?? [])
        .map((e) => byKey.get(`${e.connection_kind}:${e.connection_id}`))
        .filter((c): c is UnifiedConnection => !!c),
    [equipmentQ.data, byKey],
  );

  const available = useMemo(
    () => (connectionsQ.data ?? []).filter((c) => !equippedKeys.has(`${c.kind}:${c.connection_id}`)),
    [connectionsQ.data, equippedKeys],
  );

  function dotStyle(c: UnifiedConnection): CSSProperties {
    return { width: 6, height: 6, borderRadius: "50%", background: DOT_COLOR[equippedDotKind(c)], flexShrink: 0 };
  }

  async function addConn(c: UnifiedConnection) {
    setAdding(false);
    await equip.mutateAsync({ node_id: nodeId, connection_kind: c.kind, connection_id: c.connection_id });
  }
  async function removeConn(c: UnifiedConnection) {
    await unequip.mutateAsync({ node_id: nodeId, connection_kind: c.kind, connection_id: c.connection_id });
  }

  if (!canEdit) {
    if (equipped.length === 0) return <div style={muted}>No connections equipped</div>;
    return (
      <div>
        <div style={chips}>
          {equipped.map((c) => (
            <span key={`${c.kind}:${c.connection_id}`} style={chip} title={c.name}>
              <span style={dotStyle(c)} />
              <span style={chipName}>{c.name}</span>
            </span>
          ))}
        </div>
        <EquippedWithLegend />
      </div>
    );
  }

  return (
    <div>
      <div style={chips}>
        {equipped.map((c) => (
          <span key={`${c.kind}:${c.connection_id}`} style={chip} title={c.name}>
            <span style={dotStyle(c)} />
            <span style={chipName}>{c.name}</span>
            <button
              type="button"
              style={removeBtn}
              onClick={() => removeConn(c)}
              aria-label={`Remove ${c.name}`}
            >
              <i className="ti ti-x" style={{ fontSize: 10 }} />
            </button>
          </span>
        ))}
        {available.length > 0 && (
          <div style={{ position: "relative" }}>
            <button type="button" style={addChip} onClick={() => setAdding((v) => !v)}>
              <i className="ti ti-plus" style={{ fontSize: 11 }} /> Add
            </button>
            {adding && (
              <div style={dropdown}>
                {available.map((c) => (
                  <button
                    key={`${c.kind}:${c.connection_id}`}
                    type="button"
                    style={dropItem}
                    onClick={() => addConn(c)}
                  >
                    <span style={dotStyle(c)} />
                    <span style={dropName}>{c.name}</span>
                    <ConnectionTypeChip type={chipTypeFor(c)} size="small" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {equipped.length === 0 && available.length === 0 && (
        <div style={muted}>No connections in this workspace yet.</div>
      )}
      {equipped.length > 0 && <EquippedWithLegend />}
    </div>
  );
}

const chips: CSSProperties = { display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" };

const chip: CSSProperties = {
  fontSize: 11,
  padding: "3px 6px 3px 6px",
  borderRadius: 999,
  background: "var(--color-background-primary)",
  border: "0.5px solid var(--color-border-tertiary)",
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  color: "var(--color-text-secondary)",
  fontFamily: "var(--font-sans)",
  maxWidth: 200,
};

const chipName: CSSProperties = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const removeBtn: CSSProperties = {
  background: "transparent",
  border: "none",
  cursor: "pointer",
  color: "var(--color-text-tertiary)",
  padding: 0,
  display: "inline-flex",
  alignItems: "center",
};

const addChip: CSSProperties = {
  fontSize: 11,
  padding: "3px 10px",
  borderRadius: 999,
  background: "var(--color-background-primary)",
  border: "0.5px dashed var(--color-border-secondary)",
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const dropdown: CSSProperties = {
  position: "absolute",
  top: "calc(100% + 4px)",
  left: 0,
  zIndex: 20,
  minWidth: 220,
  maxHeight: 220,
  overflowY: "auto",
  background: "var(--color-background-primary)",
  border: "0.5px solid var(--color-border-secondary)",
  borderRadius: "var(--border-radius-sm)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
  padding: 4,
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const dropItem: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 8px",
  borderRadius: "var(--border-radius-sm)",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  textAlign: "left",
  fontFamily: "var(--font-sans)",
  width: "100%",
};

const dropName: CSSProperties = {
  flex: 1,
  fontSize: 12,
  color: "var(--color-text-primary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const muted: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-tertiary)",
  fontStyle: "italic",
};
